/**
 * One run of the document checks, from the lease on screen to findings.
 *
 * The pieces existed separately and this is the seam between them: the outline
 * builder that already reads a rendered lease, the reader that extracts one
 * figure per concept, and the comparison that lp-012 built for the counterparty
 * ladder. Nothing here decides anything — it moves a document through three
 * things that each already know their own job.
 */
import { buildFormOutline } from './form_outline';
import {
  checklistFindings,
  contradictionFindings,
  orderFindings,
  playbookFindings,
  semanticFindings,
} from './document_checks';
import { integrityFindings } from './document_integrity';

export const CHECK_UNAVAILABLE = Object.freeze({
  no_playbook:
    'There is no playbook for this kind of document yet, so there is nothing to check the figures against.',
  no_measurable_concepts:
    'No position in your playbook has numbers attached, so there is no figure to measure this document against. Add them to a concept and its figures get checked here.',
});

/**
 * @param {Document|Element} root the rendered lease
 * @returns {Array} sections, or [] when the lease has not rendered yet
 */
/**
 * Only what is actually in the document.
 *
 * The outline marks a section or paragraph `conditional` when it sits inside
 * `.lp-hide-element` — a branch of the form that is not currently switched on.
 * That text is in the projection but not in the lease anybody will read, and
 * checking it produces findings nobody can act on or even find: a numbering gap
 * at 63 because two switched-off articles are numbered 64 and 65, two articles
 * "both numbered 53" where one of them is not there, and blanks counted by the
 * dozen out of guaranty and lien-waiver exhibits this deal never included.
 *
 * A check that reports on text the drafter cannot see is worse than no check.
 */
function inTheDocument(sections) {
  return sections
    .filter(section => !section.conditional)
    .map(section => ({
      ...section,
      paragraphs: (section.paragraphs || []).filter(
        paragraph => !(paragraph && paragraph.conditional),
      ),
    }));
}

export function outlineFor(root) {
  try {
    return inTheDocument((buildFormOutline(root) || {}).sections || []);
  } catch (error) {
    // A lease mid-render has no outline, which is a reason to run later rather
    // than a reason to report a failure to the drafter.
    return [];
  }
}

/**
 * Findings, or the reason there are none.
 *
 * The distinction is the point: nothing found and nothing checked look the same
 * from a count of zero, and only one of them means the document is fine.
 */
/**
 * The families that need nothing from a server.
 *
 * Run against the same outline the reader is given, and separately from it: a
 * failed or unavailable ladder read must not take the mechanical findings down
 * with it. A drafter with no playbook still gets told about the `[TBD]` in
 * their rent clause.
 */
export function localFindings(sections) {
  return orderFindings(integrityFindings(sections));
}

/**
 * How much of the document the reading pass reached, when it stopped short.
 *
 * Null when it read everything, because a panel that reports full coverage on
 * every ordinary document trains people to stop reading the line — and the one
 * time it matters is the one time nobody looks.
 */
/**
 * The families that did not run, in the drafter's terms.
 *
 * A reader that fell over used to leave a shorter list and no explanation, and
 * a short list reads as a clean document — the one wrong conclusion available
 * here. Naming the family costs a sentence and buys the difference between "we
 * looked and found little" and "we did not finish looking".
 */
const FAMILY_NAMES = {
  playbook: 'the figures against your playbook',
  checklist: 'your checklist',
  contradiction: 'the document contradicting itself',
  semantic: 'the reading pass over the whole document',
};

function familiesThatFailed(data) {
  const statuses = data.families || {};

  return Object.keys(FAMILY_NAMES)
    .filter(name => statuses[name] === 'failed')
    .map(name => FAMILY_NAMES[name]);
}

function readCoverage(data) {
  const read = data.semanticRead;
  const total = data.semanticTotal;
  if (!total || !read || read >= total) return null;

  return { read: read, total: total };
}

export function findingsFrom(response) {
  const data = (response && response.data) || {};

  // Families that need no playbook behind them: the customer's own items, and
  // a document contradicting itself. They arrive in one response because they
  // are one model pass each over the same text, and the panel orders them
  // together — a customer's own high-severity item outranks our medium one,
  // which is the whole point of letting them set severity.
  const custom = checklistFindings(data.checklist);
  const contradictions = contradictionFindings(data.contradictions);
  const semantic = semanticFindings(data.semantic);

  // `reason` explains why the PLAYBOOK family is silent. It used to suppress
  // the whole response, which was right when the playbook family was the only
  // one that came back this way — and became wrong the moment two families
  // arrived that need no playbook at all.
  if (data.reason) {
    return {
      findings: orderFindings(custom.concat(contradictions).concat(semantic)),
      unavailable: CHECK_UNAVAILABLE[data.reason] || null,
      // lp-016 §4. How much of the document the reading pass actually reached.
      // A panel that quietly read the first hundred and twenty sections of a
      // hundred and eighty reads as one that read them all.
      readUpTo: readCoverage(data),
      didNotRun: familiesThatFailed(data),
    };
  }

  const readings = (data.readings || []).map(reading => ({
    concept: reading.concept,
    value: reading.value,
    quote: reading.quote,
    section: {
      pid: reading.sectionPid,
      number: reading.sectionNumber,
      order: reading.sectionOrder,
    },
  }));

  return {
    findings: orderFindings(
      playbookFindings(readings, data.contexts, data.concepts)
        .concat(custom)
        .concat(contradictions)
        .concat(semantic),
    ),
    unavailable: null,
    readUpTo: readCoverage(data),
    didNotRun: familiesThatFailed(data),
  };
}
